import { useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { SplitText } from "gsap/all";
import CheckBackground from "./SVGComponents/CheckBackground";
import heroImage from "../../assets/REMA/hero-image.png";
import Navbar from "./Navbar";
import { Link } from "react-router-dom";
import {
  REMA_REGISTRATION_PAGE_URL,
  REMA_SPONSORSHIP_PAGE_URL,
} from "./constants";
import NavBarSticky from "./ NavBarSticky";
import DownloadBrochureButton from "./DownloadBrochureButton";

// Register the GSAP plugin
gsap.registerPlugin(SplitText);

const Hero = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const detailsRef = useRef(null);
  const registerButtonRef = useRef(null);
  const sponsorButtonRef = useRef(null);
  const [navToggle, setNavToggle] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;

      const heroHeight = heroRef.current.offsetHeight;
      const scrollPosition = window.scrollY;

      // Hide navbar if scrolled past hero, else show
      if (scrollPosition > heroHeight) {
        setNavToggle(true);
      } else {
        setNavToggle(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useGSAP(
    () => {
      // Create timeline for coordinated animations
      const tl = gsap.timeline({ delay: 0.5 });

      // Split text for the main title
      const titleSplit = new SplitText(titleRef.current, {
        type: "words",
        linesClass: "overflow-hidden",
      });

      // Split text for the subtitle
      const subtitleSplit = new SplitText(subtitleRef.current, {
        type: "words",
      });

      // Set initial states
      gsap.set(titleSplit.words, { y: 100, opacity: 0 });
      gsap.set(subtitleSplit.words, { y: 50, opacity: 0 });
      gsap.set(detailsRef.current, { y: 30, opacity: 0 });
      gsap.set(registerButtonRef.current, { y: 40, opacity: 0, scale: 0.9 });
      gsap.set(sponsorButtonRef.current, { y: 40, opacity: 0, scale: 0.9 });

      // Animate title words with stagger
      tl.to(titleSplit.words, {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power3.out",
        stagger: 0.1,
      })

        // Animate subtitle words
        .to(
          subtitleSplit.words,
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power2.out",
            stagger: 0.05,
          },
          "-=0.3"
        )

        // Animate event details
        .to(
          detailsRef.current,
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: "power2.out",
          },
          "-=0.2"
        )

        // Animate CTA button
        .to(
          registerButtonRef.current,
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.7,
            ease: "back.out(1.7)",
          },
          "<"
        )
        .to(
          sponsorButtonRef.current,
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.7,
            ease: "back.out(1.7)",
          },
          "-=0.05"
        );

      // Cleanup function
      return () => {
        titleSplit.revert();
        subtitleSplit.revert();
      };
    },
    { scope: heroRef }
  );

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen w-full overflow-hidden bg-gray-900"
    >
      <Navbar navToggle={navToggle} />
      <NavBarSticky navToggle={navToggle} />

      {/* Content Container */}
      <div className="relative z-10 flex flex-col min-h-screen items-center justify-center">
        <div className=" flex font-sora h-[30dvh] md:h-[calc(60dvh-86px)] overflow-hidden w-full relative">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" />
          <img
            src={heroImage}
            alt="Hero Background"
            className="w-full h-full object-cover"
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 hero-bg-gradient" />
        </div>

        {/* HERO DETAILS */}
        <div className="bg-[#082421] flex-1 md:h-[50dvh] w-full flex items-center relative ">
          <div className="absolute inset-0">
            <CheckBackground />
          </div>
          <div className="container mx-auto flex font-sora text-white justify-between relative z-10 flex-col lg:flex-row p-4 py-[57px] md:p-10 md:gap-10 gap-8">
            <div className="lg:max-w-[647px]">
              <h1
                ref={titleRef}
                className="text-[32px] lg:text-[36px]  md:text-[50px] xl:text-[50px] font-bold text-balance leading-[125%]"
              >
                Real Estate Management in Africa{" "}
                <span className="text-[#CBF07F]">(REMA) 2025</span>
              </h1>

              <div className=" space-x-4 mt-[53px] items-center uppercase text-white hidden lg:flex">
                <p>November 6, 2025 </p>
                <div className="bg-[#CBF07F] w-[1px] h-[20px]" />
                <p> The Podium, Lekki, Lagos</p>
              </div>
            </div>
            <div className="lg:max-w-[380px] flex flex-col justify-between gap-10">
              <p
                ref={subtitleRef}
                className="font-bold text-[18px] md:text-[22px] leading-normal"
              >
                Structures Beyond Buildings: <br></br>
                <span className="font-light italic">
                  Managing Africa’s Urban Communities
                </span>
              </p>
              <div className="text-sm md:text-base md:gap-4 md:items-center uppercase text-white flex lg:hidden flex-col md:flex-row gap-2">
                <p>November 6, 2025 </p>
                <div className="bg-[#CBF07F] w-[1px] h-[20px] hidden md:block" />
                <p> The Podium, Lekki, Lagos</p>
              </div>
              <div className="flex flex-col md:flex-row gap-3 ">
                {/* <div ref={registerButtonRef}>
                  <Link
                    to={REMA_REGISTRATION_PAGE_URL}
                    className="bg-light-green-500 hover:bg-light-green-600 text-green-800 font-semibold py-3 px-8  transition duration-300 border-light-green-500 border ease-in-out transform  text-sm block text-center w-full  lg:w-fit"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Register now
                  </Link>
                </div>
                <div ref={sponsorButtonRef}>
                  <a
                    href={REMA_SPONSORSHIP_PAGE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" hover:bg-green-900 text-light-green-500 font-semibold py-3 px-8 transition duration-300 ease-in-out transform text-sm  w-full lg:w-fit border-light-green-500 block text-center border"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Become a sponsor
                  </a>
                </div> */}
                <div ref={registerButtonRef}>
                  <DownloadBrochureButton />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
